// src/components/EquipmentManager.js
import React, { useState, useEffect } from 'react';
import { addEquipment, getAllEquipment, updateEquipment, deleteEquipment } from '../services/equipmentService';

const EquipmentManager = () => {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    category: '',
    totalQty: 1,
    availableQty: 1,
    imageUrl: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Load equipment on component mount
  useEffect(() => {
    fetchEquipment();
  }, []);

  // Extract unique categories
  useEffect(() => {
    const uniqueCategories = [...new Set(equipment.map(item => item.category))];
    setCategories(uniqueCategories);
  }, [equipment]);

  const fetchEquipment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAllEquipment();
      
      if (response.success) {
        setEquipment(response.equipment);
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'totalQty' || name === 'availableQty' ? parseInt(value, 10) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Validate form data
    if (!formData.name || !formData.type || !formData.category) {
      setError("Please fill in all required fields");
      setLoading(false);
      return;
    }
    
    if (formData.totalQty < formData.availableQty) {
      setError("Available quantity cannot exceed total quantity");
      setLoading(false);
      return;
    }
    
    try {
      if (editingId) {
        // Update existing equipment
        await updateEquipment(editingId, {
          ...formData,
          status: formData.availableQty > 0 ? 'Available' : 'Out of Stock'
        });
      } else {
        // Add new equipment
        await addEquipment({
          ...formData,
          status: formData.availableQty > 0 ? 'Available' : 'Out of Stock'
        });
      }
      
      // Reset form and refresh equipment list
      setFormData({
        name: '',
        type: '',
        category: '',
        totalQty: 1,
        availableQty: 1,
        imageUrl: ''
      });
      setEditingId(null);
      await fetchEquipment();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    // Set form data with the item's values
    setFormData({
      name: item.name || '',
      type: item.type || '',
      category: item.category || '',
      totalQty: item.totalQty || 1,
      availableQty: item.availableQty || 1,
      imageUrl: item.imageUrl || ''
    });
    
    // Set the editing ID
    setEditingId(item.id);
    
    // Show the form
    setShowForm(true);
    
    // Scroll to the form
    setTimeout(() => {
      const formElement = document.getElementById('equipment-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleDelete = async (id) => {
    try {
      await deleteEquipment(id);
      // Refresh equipment list after deletion
      fetchEquipment();
    } catch (error) {
      // Handle error silently
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      type: '',
      category: '',
      totalQty: 1,
      availableQty: 1,
      imageUrl: ''
    });
    setEditingId(null);
  };

  // Filter equipment by selected category
  const filteredEquipment = selectedCategory === 'all' 
    ? equipment 
    : equipment.filter(item => item.category === selectedCategory);

  // Group equipment by category
  const groupedEquipment = equipment.reduce((groups, item) => {
    const category = item.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {});

  return (
    <div style={{ padding: '20px', backgroundColor: '#1e1e1e', color: '#e0e0e0' }}>
      <h2 style={{ marginBottom: '30px' }}>Equipment Management</h2>
      
      {/* Add Equipment Button */}
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            backgroundColor: '#3d7cf5',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {showForm ? 'Cancel Adding Equipment' : 'Add New Equipment'}
        </button>
      </div>
      
      {/* Equipment Form */}
      <div 
        id="equipment-form"
        style={{ 
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#252525',
          borderRadius: '8px',
          display: showForm ? 'block' : 'none'
        }}
      >
        <h3>{editingId ? 'Edit Equipment' : 'Add New Equipment'}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Name:
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#333',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  marginTop: '5px'
                }}
                required
              />
            </label>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Category:
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#333',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  marginTop: '5px'
                }}
                list="categories"
                required
              />
              <datalist id="categories">
                {categories.map((category, index) => (
                  <option key={index} value={category} />
                ))}
              </datalist>
            </label>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Type:
              <input
                type="text"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#333',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  marginTop: '5px'
                }}
                required
              />
            </label>
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Image URL:
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#333',
                  border: '1px solid #444',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  marginTop: '5px'
                }}
                placeholder="https://example.com/image.jpg"
              />
            </label>
          </div>
          
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Total Quantity:
                <input
                  type="number"
                  name="totalQty"
                  value={formData.totalQty}
                  onChange={handleInputChange}
                  min="1"
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#333',
                    border: '1px solid #444',
                    borderRadius: '4px',
                    color: '#e0e0e0',
                    marginTop: '5px'
                  }}
                  required
                />
              </label>
            </div>
            
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>
                Available Quantity:
                <input
                  type="number"
                  name="availableQty"
                  value={formData.availableQty}
                  onChange={handleInputChange}
                  min="0"
                  max={formData.totalQty}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#333',
                    border: '1px solid #444',
                    borderRadius: '4px',
                    color: '#e0e0e0',
                    marginTop: '5px'
                  }}
                  required
                />
              </label>
            </div>
          </div>
          
          {error && (
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#2c1215', 
              color: '#ef4444',
              borderRadius: '4px',
              marginBottom: '15px'
            }}>
              {error}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="submit" 
              disabled={loading}
              style={{ 
                backgroundColor: '#3d7cf5', 
                color: 'white', 
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {editingId ? 'Update Equipment' : 'Add Equipment'}
            </button>
            
            {editingId && (
              <button 
                type="button" 
                onClick={handleCancel}
                style={{ 
                  backgroundColor: '#2d2d2d', 
                  color: '#e0e0e0', 
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: '1px solid #444'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
      
      {/* Equipment List */}
      <div style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Equipment Inventory</h3>
          
          {/* Category Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ marginRight: '10px' }}>Filter by Category:</label>
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '8px',
                backgroundColor: '#333',
                border: '1px solid #444',
                borderRadius: '4px',
                color: '#e0e0e0'
              }}
            >
              <option value="all">All Categories</option>
              {categories.map((category, index) => (
                <option key={index} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
        
        {loading && <p>Loading...</p>}
        
        {equipment.length > 0 ? (
          <div>
            {/* If a specific category is selected, show only that category */}
            {selectedCategory !== 'all' ? (
              <div style={{ 
                marginTop: '15px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid #333'
              }}>
                <div style={{ 
                  padding: '12px 15px', 
                  backgroundColor: '#252525',
                  borderBottom: '1px solid #333',
                  fontWeight: 'bold'
                }}>
                  {selectedCategory}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#252525' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #333' }}>Name</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #333' }}>Type</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Total</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Available</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Status</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEquipment.map((item) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #333' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <div 
                            style={{ 
                              position: 'relative',
                              cursor: item.imageUrl ? 'pointer' : 'default'
                            }}
                            onMouseEnter={() => setHoveredItem(item)}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            {item.name}
                            {hoveredItem?.id === item.id && item.imageUrl && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 1000,
                                backgroundColor: '#252525',
                                padding: '8px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                width: '200px',
                                marginTop: '8px'
                              }}>
                                <img 
                                  src={item.imageUrl} 
                                  alt={item.name}
                                  style={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: '4px'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>{item.type}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.totalQty}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.availableQty}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <span style={{ 
                            padding: '4px 8px',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            backgroundColor: item.availableQty > 0 ? 'var(--success-bg)' : 'var(--danger-bg)',
                            color: item.availableQty > 0 ? 'var(--success-color)' : 'var(--danger-color)'
                          }}>
                            {item.availableQty > 0 ? 'Available' : 'Out of Stock'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                            <button 
                              onClick={() => handleEdit(item)}
                              style={{ 
                                backgroundColor: '#3d7cf5', 
                                color: 'white', 
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDelete(item.id)}
                              style={{ 
                                backgroundColor: '#2c1215', 
                                color: '#ef4444', 
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* If "All Categories" is selected, show grouped equipment */
              <div>
                {Object.keys(groupedEquipment).map(category => (
                  <div 
                    key={category} 
                    style={{ 
                      marginBottom: '20px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #333'
                    }}
                  >
                    <div style={{ 
                      padding: '12px 15px', 
                      backgroundColor: '#252525',
                      borderBottom: '1px solid #333',
                      fontWeight: 'bold'
                    }}>
                      {category}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#252525' }}>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #333' }}>Name</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #333' }}>Type</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Total</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Available</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Status</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #333' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedEquipment[category].map((item) => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '12px 8px' }}>
                              <div 
                                style={{ 
                                  position: 'relative',
                                  cursor: item.imageUrl ? 'pointer' : 'default'
                                }}
                                onMouseEnter={() => setHoveredItem(item)}
                                onMouseLeave={() => setHoveredItem(null)}
                              >
                                {item.name}
                                {hoveredItem?.id === item.id && item.imageUrl && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1000,
                                    backgroundColor: '#252525',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    width: '200px',
                                    marginTop: '8px'
                                  }}>
                                    <img 
                                      src={item.imageUrl} 
                                      alt={item.name}
                                      style={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px' }}>{item.type}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.totalQty}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>{item.availableQty}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                              <span style={{ 
                                padding: '4px 8px',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                backgroundColor: item.availableQty > 0 ? 'var(--success-bg)' : 'var(--danger-bg)',
                                color: item.availableQty > 0 ? 'var(--success-color)' : 'var(--danger-color)'
                              }}>
                                {item.availableQty > 0 ? 'Available' : 'Out of Stock'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                <button 
                                  onClick={() => handleEdit(item)}
                                  style={{ 
                                    backgroundColor: '#3d7cf5', 
                                    color: 'white', 
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => handleDelete(item.id)}
                                  style={{ 
                                    backgroundColor: '#2c1215', 
                                    color: '#ef4444', 
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p style={{ marginTop: '15px' }}>No equipment found. Add some equipment using the form above.</p>
        )}
      </div>
    </div>
  );
};

export default EquipmentManager;
